import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/services/httpClient";

export default function ResetPassword({
  t,
  language = "fr",
  onLanguageChange,
}) {
  const copy = t?.resetPassword || {};
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!token) {
      setStatus("error");
      setMessage(copy.invalidLink || "Lien de réinitialisation invalide.");
      return;
    }

    if (password.length < 8) {
      setStatus("error");
      setMessage(copy.tooShort || "Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (password !== confirmation) {
      setStatus("error");
      setMessage(copy.mismatch || "Les deux mots de passe ne correspondent pas.");
      return;
    }

    setStatus("loading");

    try {
      const response = await apiClient.post("/api/auth/reset-password", {
        token,
        newPassword: password,
      });

      if (response.status >= 200 && response.status < 300) {
        setStatus("success");
        setPassword("");
        setConfirmation("");
        setMessage(copy.successMessage || "Votre mot de passe a été modifié.");
        return;
      }

      setStatus("error");

      if (response.status === 400) {
        setMessage(
          copy.expiredLink || "Ce lien de réinitialisation est invalide ou a expiré."
        );
      } else {
        setMessage(
          response.data?.detail ||
            copy.changeError || "Impossible de modifier le mot de passe."
        );
      }
    } catch (error) {
      console.error("[ResetPassword] Request failed:", error);
      setStatus("error");
      setMessage(
        copy.genericError || "Une erreur est survenue. Veuillez réessayer."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-white text-3xl font-semibold tracking-wide">
            Hearen
          </div>
          <p className="text-gray-400 mt-3">
            {copy.subtitle || "Réinitialisation du mot de passe"}
          </p>

          <div
            className="mt-4 flex items-center justify-center gap-2 text-sm"
            aria-label="Language"
          >
            <button
              type="button"
              onClick={() => onLanguageChange?.("fr")}
              className={
                language === "fr"
                  ? "text-white font-semibold"
                  : "text-gray-500 hover:text-gray-300"
              }
            >
              FR
            </button>

            <span className="text-gray-600">·</span>

            <button
              type="button"
              onClick={() => onLanguageChange?.("en")}
              className={
                language === "en"
                  ? "text-white font-semibold"
                  : "text-gray-500 hover:text-gray-300"
              }
            >
              EN
            </button>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          {status === "success" ? (
            <div className="text-center">
              <h1 className="text-white text-xl font-semibold mb-3">
                {copy.successTitle || "Mot de passe modifié"}
              </h1>

              <p className="text-gray-300">
                {message}
              </p>

              <p className="text-gray-400 text-sm mt-4">
                {copy.successReturn ||
                  "Vous pouvez maintenant retourner dans Hearen et vous connecter avec votre nouveau mot de passe."}
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-white text-xl font-semibold mb-2">
                {copy.title || "Choisir un nouveau mot de passe"}
              </h1>

              <p className="text-gray-400 text-sm mb-6">
                {copy.hint || "Votre nouveau mot de passe doit contenir au moins 8 caractères."}
              </p>

              {!token ? (
                <div className="text-red-300 text-sm">
                  {copy.invalidLink || "Ce lien de réinitialisation est invalide."}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-gray-300 text-sm mb-2"
                    >
                      {copy.newPassword || "Nouveau mot de passe"}
                    </label>

                    <input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-white outline-none focus:border-white/30"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="confirmation"
                      className="block text-gray-300 text-sm mb-2"
                    >
                      {copy.confirmPassword || "Confirmer le mot de passe"}
                    </label>

                    <input
                      id="confirmation"
                      type="password"
                      autoComplete="new-password"
                      value={confirmation}
                      onChange={(event) => setConfirmation(event.target.value)}
                      className="w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-white outline-none focus:border-white/30"
                      required
                    />
                  </div>

                  {message && (
                    <div className="text-red-300 text-sm">
                      {message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full rounded-xl bg-[#6d5dfc] text-white font-medium py-3 disabled:opacity-60"
                  >
                    {status === "loading"
                      ? (copy.loading || "Modification…")
                      : (copy.submit || "Modifier mon mot de passe")}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
